import React, { Component } from 'react';
import qs from 'qs';
import scrollTo from 'scroll-to-element';

import './compiled/Form.css';
import * as api from './api';

import FormElement, { getKeys, validate } from './FormElement';

const SUPPORT_EMAIL = 'notify@gofullpage.com';

class Form extends Component {
  // Constructor
  constructor(props) {
    super();

    this.state = {
      focus_index: -1,
      is_submitting: false,
      has_submitted: false,
      values: _getValues(props.fields),
      error_msg: '',
    };

    this.elements = [];
  }

  // Life-cycle
  componentDidMount() {
    if (!this.props.disableAutofocus)
      this.triggerFocus(0);
    this._checkIsValid();
  }

  componentDidUpdate(prev_props, prev_state) {
    if (!this.props.disableAutofocus && prev_state.is_valid && !this.state.is_valid) {
      scrollTo(this.form, { offset: -30, duration: 600 });
    }
  }

  // Handlers
  handleNext = (e, back, isEnter) => {
    // index can be -1 for start w/o focus through element.length for submit button
    let index = _constrain(
      this.state.focus_index + (back ? -1 : 1),
      -1,
      this.elements.length
    );

    this.triggerFocus(index);

    // do the submit if we're in the last input before the submit
    if (isEnter && index === this.elements.length) {
      this.handleSubmit();
    }
  };

  handleFocus = (e, index) => {
    this.setState({ focus_index: index });
  };

  handleButtonKeyDown = (e) => {
    let keys = getKeys(e);
    if (keys.vertical) {
      this.handleNext(e, keys.up);
    }
  };

  handleChange = (e, i) => {
    let target = e.target;
    let value = target.value;
    this.setState((state, props) => {
      let values = state.values.map((val, j) => (i === j ? value : val));
      return { values };
    });
    this._checkIsValid();
  };

  handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (this.state.is_submitting) {
      return;
    }

    this.setState({ is_submitting: true, has_submitted: true, error_msg: '' });

    const _reset = () => this.setState({ is_submitting: false });

    // get data
    let data = {};
    this.props.fields.forEach((f, i) => {
      data[f.name] = this.state.values[i];
    });
    const issue = this.props.issue
    if (issue) {
      data.issue_name = issue.name
      data.issue_category = issue.category
      data.issue_tag = issue.tag
    }
    data.language = navigator.language || ''

    // validate
    let is_valid = this._checkIsValid(true);

    if (!is_valid) {
      _reset();
      return;
    }

    api
      .postIssue(data)
      .then(({ data, resp, status }) => {
        // Go to success!
        let history = this.props.history;
        history.push('/success'); // TODO - avoid having to hardcode URLs?
      })
      .catch((err) => {
        try {
          console.error(err);
        } catch (e) { }
        let msg = `Error posting your issue. Please try again or contact ${SUPPORT_EMAIL}`;
        this.setState({ error_msg: msg });
        _reset();
      });
  };

  // Triggers
  triggerFocus = (index) => {
    if (index >= this.elements.length) {
      this.submit.focus();
    } else {
      let element = this.elements[index];
      if (element) {
        element.triggerFocus();
      }
    }
  };

  // Helpers
  _checkIsValid(force) {
    let is_valid =
      (!force && !this.state.has_submitted) ||
      this.props.fields.every((f, i) => validate(f, this.state.values[i]));

    this.setState({ is_valid: is_valid });
    return is_valid;
  }

  // Render
  render() {
    // do no mutate fields!
    let fields = this.props.fields.map((field, i) =>
      Object.assign({}, field, { value: this.state.values[i] })
    );
    let visible_fields = fields.filter((x) => !x.hidden);

    return (
      <form
        className="Form"
        onSubmit={this.handleSubmit}
        ref={(node) => (this.form = node)}
      >
        <div className={`errors ${this.state.is_valid ? 'hide' : ''}`}>
          {this.state.error_msg || 'Please fix the errors below.'}
        </div>
        {visible_fields.map((f, i) => (
          <FormElement
            key={`${f.name}`}
            ref={(node) => (this.elements[i] = node)}
            onChange={(e) => this.handleChange(e, i)}
            onFocus={(e) => this.handleFocus(e, i)}
            onNext={this.handleNext}
            is_dirty={this.state.has_submitted}
            is_disabled={this.state.is_submitting}
            {...f}
          />
        ))}
        <div className="buttons">
          <input
            ref={(node) => (this.submit = node)}
            type="submit"
            className="button-primary left"
            value={this.state.is_submitting ? 'Loading…' : 'Submit'}
            disabled={this.state.is_submitting}
            onKeyDown={this.handleButtonKeyDown}
          />
        </div>
      </form>
    );
  }
}

// Helper functions

const _constrain = (x, min, max) => Math.max(min, Math.min(max, x));

const _getValues = (fields) => {
  let query = window.location.search.substring(1);
  let query_data = qs.parse(query);

  return fields.map((f) => {
    let value = query_data[f.query];
    if (value) {
      if (f.query_encoding) {
        switch (f.query_encoding) {
          case 'base64':
            value = _b64decode(value, f.query);
            break;
          default:
            console.error(
              `[_getValues] unknown query encoding ${f.query_encoding}`
            );
            break;
        }
      }
    } else {
      value = f.value;
    }
    return value;
  });
};

const _b64decode = (val, key) => {
  // val: string - the value to decoded
  // key: string (optional) - for debugging

  val = val || '';

  // HACK - in case of double encoding bug - a base64 string should never have
  // a % in it, so it should be OK to try and decode it an extra time if we see one
  //
  if (val.indexOf('%') !== -1) {
    try {
      val = decodeURIComponent(val);
    } catch (e) {
      console.error(
        `[b64decode] unable decode potential extra % for ${val}: ${e}`
      );
    }
  }

  // decode, but also try stripping letters in case the result
  // was split at a bad location...
  let error = null;
  let pad = 4;
  val = val.replace(/=+$/, '');

  for (let i = 0; i <= pad; i++) {
    let t = val.substring(0, val.length - i);
    let m = t % pad;
    if (m !== 0) {
      t += '='.repeat(pad - m);
    }
    try {
      return atob(t);
    } catch (e) {
      if (error === null) {
        console.log('truncated', key);
        error = e;
      }
    }
  }

  console.error(`[b64decode] failed for ${val.length}: ${error}`, val);

  return val;
};

export default Form;
